import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchBackButton } from '@/components/ui/search-back-button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { ActivityTimeline } from '@/components/ActivityTimeline';
import { useCustomer } from '@/hooks/useCustomer';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground uppercase tracking-wider">
        {label}
      </label>
      <p className="mt-1 break-words text-sm text-foreground">{value ?? '—'}</p>
    </div>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useCustomer(id);

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="h-14 flex items-center gap-3 border-b border-border px-4 shrink-0 md:px-6">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-5 w-40" />
        </div>
        <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="h-full flex flex-col">
        <div className="h-14 flex items-center gap-3 border-b border-border px-4 shrink-0 md:px-6">
          <SearchBackButton />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Customer not found</p>
        </div>
      </div>
    );
  }

  const { customer, orders } = data;
  const name =
    [customer.first_name, customer.last_name].filter(Boolean).join(' ') ||
    customer.email ||
    'Customer';
  const stateVariant =
    customer.state === 'enabled'
      ? 'success'
      : customer.state === 'disabled'
        ? 'destructive'
        : 'secondary';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex min-h-14 flex-wrap items-center gap-2 border-b border-border px-4 py-2 shrink-0 md:px-6">
        <SearchBackButton />
        <h1 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
          {name}
        </h1>
        {customer.state && (
          <Badge variant={stateVariant} className="capitalize">
            {customer.state}
          </Badge>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer info */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-sm">Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Name" value={name} />
                <Field label="Email" value={customer.email} />
                <Field label="Phone" value={customer.phone} />
                <Field
                  label="Customer Since"
                  value={formatDate(customer.shopify_created_at)}
                />
              </div>
              {customer.tags && customer.tags.length > 0 && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">
                    Tags
                  </label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {customer.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-[10px]">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Spend summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Orders</span>
                <span className="text-foreground font-mono">
                  {customer.orders_count}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Spent</span>
                <span className="text-foreground font-mono">
                  {formatCurrency(customer.total_spent)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Orders */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Orders ({orders.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {orders.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                No orders for this customer yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow
                      key={order.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/orders/${order.shopify_order_id}`)}
                    >
                      <TableCell className="text-sm text-foreground">
                        {order.name ?? `#${order.order_number}`}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDateTime(order.processed_at ?? order.shopify_created_at)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground capitalize">
                        {order.financial_status ?? '—'}
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono">
                        {formatCurrency(order.total_price)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Activity timeline */}
        <ActivityTimeline customerId={customer.shopify_customer_id} />
      </div>
    </div>
  );
}
